import {
    RiDeleteBinLine, RiEdit2Line, RiGroupLine,
    RiStackLine,
} from "@remixicon/react";
import React from "react";
import {
    GetQueueDocument,
    GetQueuesDocument,
    Queue,
    TransferSettingsDocument,
    useDeleteQueueMutation,
    useGetQueuesQuery,
} from "../../../../__generated__/api";
import { ReactComponent as GroupsList } from "../../../../assets/icons/groups-list.svg";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import ChannelTags from "../../../../components/pickers/channel-tags/ChannelTags";
import Badge from "../../../../components/theme/badge/Badge";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting from "../../../../contexts/Routing";

const TableContainer = createTableContainer<Queue>();

export default function QueueTable() {
    useArticleContext("urn:fq:article:8c9b738bc4234e6ca05aece49ebfafb5");

    const { data, error, loading: isLoading } = useGetQueuesQuery({
        fetchPolicy: "no-cache",
    });

    const { historyPush, createHistoryPush, Paths } = useRouting();

    const [deleteQueue] = useDeleteQueueMutation();

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Queue>(
        async group => {
            await deleteQueue({
                refetchQueries: [
                    { query: GetQueueDocument },
                    { query: GetQueuesDocument },
                    { query: TransferSettingsDocument },
                ],
                variables: {
                    id: group.id,
                },
            });
        },
        user => user.name,
        "Delete Queue",
        "If you delete this queue, any tickets assigned to that will be unavailable. Do you want to delete",
        "Remove",
    );

    return (
        <SettingsContainer navigation="none">
            <SettingsContainer.Section title="Queues" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    title="Queues"
                    description="The Queue feature helps streamline ticket management by
                                ensuring each team only sees tickets relevant to them. This is
                                 especially useful if you have multiple teams, like IT and
                                 People teams, who need to focus on their own tasks without
                                 cross-interference. With our flexible options, you can
                                 effortlessly assign tickets to the right queues through
                                 Channel Groups, automations, or by routing based on email
                                 aliases or Slack channels."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isFullWidth
                >
                    <TableContainer data={data?.queues as Queue[] ?? []} isLoading={isLoading} error={error}>
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="The Queue feature helps streamline ticket management by
                                ensuring each team only sees tickets relevant to them. This is
                                 especially useful if you have multiple teams, like IT and
                                 People teams, who need to focus on their own tasks without
                                 cross-interference. With our flexible options, you can
                                 effortlessly assign tickets to the right queues through
                                 Channel Groups, automations, or by routing based on email
                                 aliases or Slack channels."
                                title="Queues"
                                icon={<RiStackLine />}
                                onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                    setting: "queues",
                                    id: "new",
                                })}
                                buttonText="Add Queue"
                            />
                        </TableContainer.Empty>
                        <TableContainer.TableControls>

                            <TableContainer.TableControls.Search
                                search={(query, group) => group.name?.toLocaleLowerCase().includes(query)
                                                    || group.description?.toLocaleLowerCase().includes(query)
                                                    || group.channelTags?.some(tag => tag.toLocaleLowerCase().includes(query))
                                                    || group.members?.length === parseInt(query, 10)}
                            />
                            <TableContainer.TableControls.Seperator />

                            <TableContainer.TableControls.Filters />

                            <TableContainer.TableControls.Filter
                                defaultValue={[] as string[]}
                                deserialize={TableContainer.TableControls.Filter.Serializers.deserializeArray}
                                serialize={TableContainer.TableControls.Filter.Serializers.serializeArray}
                                filterKey="tags"
                                onFilter={(item, selected) => selected.isEmpty || selected.union(item.channelTags).isNotEmpty}
                            >
                                {(currentValue, setValue) => (
                                    <ChannelTags
                                        isMulti
                                        icon={<GroupsList />}
                                        onSelected={setValue}
                                        value={currentValue}
                                        shouldShowDropdown
                                        searchMinimum={5}
                                        toggleText="Channel Groups"
                                        canSelect
                                    />
                                )}

                            </TableContainer.TableControls.Filter>
                            <TableContainer.TableControls.SortBy />
                            <TableContainer.TableControls.Create
                                text="Add Queue"
                                onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                    setting: "queues",
                                    id: "new",
                                })}
                            />
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.TableValue
                                columnKey="name"
                                title="Queue Name"
                                sortBy={item => item.name?.toLowerCase()}
                            >
                                {item => (
                                    <>
                                        {item.name}
                                        {" "}
                                        {item.isDefault && (
                                            <Badge variant="primary">Default</Badge>
                                        )}
                                    </>
                                )}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="description"
                                title="Description"
                                sortBy={item => item.description?.toLowerCase()}
                            >
                                {item => item.description}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="members"
                                title="Members"
                                sortBy={item => item.members?.length}
                            >
                                {item => (
                                    <Badge
                                        variant="gray"
                                        prefixIcon={<RiGroupLine />}
                                    >
                                        {item.members?.length}
                                    </Badge>
                                )}
                            </TableContainer.TableValue>

                            <TableContainer.TableValue
                                columnKey="channelTags"
                                title="Channel Groups"
                                sortBy={item => item.channelTags?.length}
                            >
                                {item => item.channelTags?.map(tag => (
                                    <Badge variant="primary" className="m3e-1" key={tag}>
                                        {tag}
                                    </Badge>
                                ))}
                            </TableContainer.TableValue>

                            <TableContainer.Action
                                icon={<RiEdit2Line />}
                                altText="Edit Queue"
                                onClick={queue => historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                    setting: "queues",
                                    id: queue.id,
                                })}
                            />
                            <TableContainer.Action
                                icon={<RiDeleteBinLine />}
                                altText="Delete Queue"
                                onClick={setDeleting}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                    {deleteElement}
                </SettingsContainer.Setting>

            </SettingsContainer.Section>
        </SettingsContainer>
    );
}
